import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { ColWithGap } from '@components/styles.tsx';
import SvgWarningExclamation from '@components/ringil3/icons/WarningExclamation.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import { LabeledChildren } from '@components/ringil3/Containers/LabeledChildren.tsx';
import ForExpedition from '@icons/ForExpedition.tsx';
import Message from '@icons/Message.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  isValidPhoneNumber,
  isValidStringParameter,
} from '@components/ringil3/Features/Expedition/Sidebars/utils/validation.ts';


interface ComponentProps {
  reservationId: string;
  applicationId: string;
  phoneNumber: string;
  ramp: string;
}

export const useMessengerTimeslotGoToRampSidebar = () => {
    const [visible, setVisible] = useState(false);

    const MessengerTimeslotGoToRampSidebar: React.FC<ComponentProps> = ({
                                                                          reservationId,
                                                                          applicationId,
                                                                          phoneNumber,
                                                                          ramp,
                                                                        }) => {

      const { tr } = useTranslationLgs();
      const [validPhoneNumber, setValidPhoneNumber] = useState(isValidPhoneNumber(phoneNumber));
      const [validRamp, setValidRamp] = useState(isValidStringParameter(ramp));
      const [loading, setLoading] = useState(false);

      const { requestTimeslotGoToRampMessage } = useContext(ApiContext);

      useEffect(() => {
        setValidPhoneNumber(isValidPhoneNumber(phoneNumber));
        setValidRamp(isValidStringParameter(ramp));
      }, [phoneNumber, ramp]);


      const messageText = tr('MessengerRequestTimeslotGoToRamp.messageFormat', 'Please proceed to {{ramp}} for reservation {{applicationId}}.', {
        applicationId: applicationId,
        ramp: ramp,
      });

      const sendMessage = () => {
        setLoading(true);
        requestTimeslotGoToRampMessage(
          {
            phoneNumber: phoneNumber,
            reservationId: reservationId,
          },
          () => {
            setLoading(false);
            setVisible(false);
          },
          null, { });
      };

      return (
        <>
          <RingilSidebar
            visible={visible}
            onBack={() => setVisible(false)}
            onContinue={() => {
              sendMessage();

            }}
            heading={tr(`MessengerRequestTimeslotGoToRamp.heading`, `Request driver arrival`)}
            showSaveButton={validPhoneNumber}
            continueLabel={tr(`MessengerRequestTimeslotGoToRamp.continue`, `Send`)}
            continueIcon={<WhiteMessage />}
            loading={loading}
          >

            <ColWithGap>
              {!validPhoneNumber && (
                <div>
                  <COInlineMessage variant={'error'} icon={<SvgWarningExclamation />}>
                    {tr('MessengerRequestTimeslotGoToRamp.invalidPhoneNumber', 'Cannot request driver due to missing or invalid phone number')}
                  </COInlineMessage>
                </div>
              )}
              {!validRamp && (
                <COInlineMessage variant={'error'} icon={<SvgWarningExclamation />}>
                  {tr('MessengerRequestTimeslotGoToRamp.invalidRamp', 'Cannot request driver due to missing ramp information')}
                </COInlineMessage>
              )}
            </ColWithGap>
            {validPhoneNumber && validRamp && (
              <ColWithGap>
                <LabeledChildren
                  label={tr(`MessengerRequestTimeslotGoToRamp.forExpedition`, `For expedition`)}
                  icon={<ForExpedition />}

                >
                  <StuledApplicationIdDiv>
                    {applicationId}
                  </StuledApplicationIdDiv>
                </LabeledChildren>
                <LabeledChildren
                  label={tr('MessengerRequestTimeslotGoToRamp.sendMessageToNumber', 'Send message to number')}
                >
                  <StyledValueDiv>{phoneNumber}</StyledValueDiv>
                </LabeledChildren>
                <LabeledChildren
                  label={tr('MessengerRequestTimeslotGoToRamp.messageText', 'Request driver arrival message')}
                >
                  <StyledValueDiv>{messageText}</StyledValueDiv>
                </LabeledChildren>
              </ColWithGap>
            )}

          </RingilSidebar>
        </>);

    };

    return { MessengerTimeslotGoToRampSidebar: MessengerTimeslotGoToRampSidebar, setVisible };
  }
;

const WhiteMessage = styled(Message)`
  path {
    fill: white;
  }
`;

const StyledValueDiv = styled.div`
  display: flex;
  height: 40px;
  padding: 10px 12px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border: 1px solid var(--content-quaternary, #E7EBEE);
  background: var(--base-basetertiary, #F7F7F7);
`;

const StuledApplicationIdDiv = styled.div`
  color: var(--content-primary, #343333);

  /* Basic Bold */
  font-family: "DM Sans", serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.16px;
`;